// LB-16 §8.5 (epic D35): client mirror of app/homes/listing_terms.py.
// The server lock is authoritative; the builder only renders the terms
// read-only (and stops autosaving them) so an issuer is not left fighting
// 400s on a live listing.

export const TERMS_LOCKED_LIVE_CODE = "terms_locked_live";

// One copy everywhere (server message included).
export const TERMS_LOCKED_BANNER =
  "Terms are locked on a live offering. Contact Invown to change terms.";

// Product decision (epic owner, 2026-10-02): approved listings only.
// Coming-soon pages are usually built before the Form C is filed.
export function isLiveListing(home) {
  return Boolean(home && home.approved);
}

// Reuses the store's `user/has_role` getter; a store without a loaded user
// (the getter dereferences state.user) counts as "not admin".
export function isAdminViewer(store) {
  try {
    const hasRole = store && store.getters && store.getters["user/has_role"];
    return typeof hasRole === "function" ? Boolean(hasRole("admin")) : false;
  } catch (e) {
    return false;
  }
}

export function areTermsLockedForViewer(home, store) {
  return isLiveListing(home) && !isAdminViewer(store);
}

// The server message for a 400 terms_locked_live, or null for any other
// error. Lets existing error banners show the lock copy verbatim.
export function termsLockedMessage(error) {
  const data = error && error.response && error.response.data;
  if (data && data.code === TERMS_LOCKED_LIVE_CODE) {
    return data.message || TERMS_LOCKED_BANNER;
  }
  return null;
}

// The locked field names from a terms_locked_live 400 ([] otherwise).
export function termsLockedFields(error) {
  const data = error && error.response && error.response.data;
  if (data && data.code === TERMS_LOCKED_LIVE_CODE && Array.isArray(data.fields)) {
    return data.fields;
  }
  return [];
}

// Issuer-facing names for the locked keys in a terms_locked_live 400.
export const TERM_FIELD_LABELS = Object.freeze({
  cash_goal: "Target raise",
  min_cash_goal: "Minimum to close",
  max_cash_goal: "Maximum raise",
  has_lower_minimum_to_close: "Lower minimum to close",
  accepts_above_target: "Above-target investments",
  oversubscription_allocation_method: "Oversubscription allocation",
  oversubscription_allocation_details: "Oversubscription details",
  debt_cash_goal: "Debt raise",
  minimum_investment: "Minimum investment",
  minimum_investment_reg_cf: "Reg CF minimum investment",
  minimum_investment_rule_506c: "506(c) minimum investment",
  share_price: "Share price",
  investment_multiple: "Investment increment",
  valuation: "Valuation",
  interest: "Interest",
  term_of_investment: "Term",
  funding_round_end: "Closing date",
  economic_track_configs: "Per-track terms",
  regulation_used: "Exemption",
  funding_model: "Financing model",
  description_of_securities: "Description of securities",
  money_allocation: "Use of funds",
  min_money_allocation: "Use of funds (minimum)",
  max_money_allocation: "Use of funds (maximum)",
  debt_money_allocation: "Use of funds (debt)",
  name: "Listing URL",
  signature_fields: "Signature fields",
  agreement_template: "Subscription agreement",
  agreement_template_506c: "506(c) subscription agreement",
  agreement_template_506c_legacy: "506(c) subscription agreement",
  agreement_template_reg_cf_merge: "Reg CF subscription agreement",
  subscription_signature_render_mode: "Subscription agreement format",
  subscription_signature_variant_policy: "Subscription agreement format",
});

export function termFieldLabel(field) {
  return TERM_FIELD_LABELS[field] || String(field).replace(/_/g, " ");
}

// LB-26 P1-1: issuer-facing names for every key the builder and the admin
// editors send to update_home, so the frozen-listing dialog lists "Reg CF
// close date: 12/31/2026", never `economic_track_configs[1].funding_round_end`.
export const FIELD_LABELS = Object.freeze({
  ...TERM_FIELD_LABELS,
  funding_round_end: "Close date",
  // Build Listing
  investment_overview: "Offering overview",
  primary_investment_objective: "Primary investment objective",
  property_type: "Property type",
  funding_purpose: "Funding purpose",
  additional_data: "Details",
  contract: "Purchase contract",
  overview_video: "Overview video",
  webinar: "Webinar",
  supporting_videos: "Supporting videos",
  supporting_documents: "Supporting documents",
  kpis: "Key metrics",
  investment_structure: "Investment structure",
  top_metrics: "Top metrics",
  at_a_glance: "At a glance",
  tab_order: "Tab order",
  faqs: "FAQs",
  custom_tabs: "Custom tabs",
  // Paperwork
  form_c_url: "Form C link",
  sec_file_number: "SEC file number",
  issuer_cik: "CIK",
  form_d_url: "Form D link",
  form_d_file_number: "Form D file number",
  is_first_time_reg_cf_issuer: "First-time Reg CF issuer",
  prior_12mo_reg_cf_raised_cents: "Reg CF raised in the last 12 months",
  risk_factors: "Risk factors",
  custom_risks: "Your own risks",
  // Propose Terms
  security_type: "Security type",
  security_class_name: "Security class name",
  voting_rights: "Voting rights",
  pricing_method: "Pricing method",
  security_terms: "Security terms",
  allow_investment_cancellation: "506(c) investors can cancel",
  use_of_funds_json: "Use of funds",
  min_use_of_funds_json: "Use of funds (minimum)",
  max_use_of_funds_json: "Use of funds (maximum)",
  // Determine Equity (the labels on that screen)
  debt: "Mortgage balance",
  additional_debt: "Other debt balance",
  rental_details: "Annual rental income",
  // Address / headline / bio
  headline: "Headline",
  zipcode: "ZIP code",
  street: "Street",
  city: "City",
  state: "State",
  address: "Address",
  company: "Company",
  profile: "Your profile",
  preferred_funding_resource: "How you plan to raise",
  // Admin editors
  disclosures: "Material risks",
  investment_reasons: "Reasons to invest",
  card_details: "Card details",
  approved: "Approved",
  submitted: "Submitted",
  coming_soon: "Coming soon",
  escrow: "Escrow",
  escrow_used: "Escrow used",
  rule_506c_account: "506(c) Enterprise account",
  neighborhood: "Neighborhood",
});

// LB-26 R2-A: option values shown by the label the issuer picked
// ("Funding purpose: Rehab", never "rehab").
export const OPTION_LABELS = Object.freeze({
  funding_purpose: Object.freeze({ purchase: "Purchase", rehab: "Rehab", Build: "Build", build: "Build" }),
  description_of_securities: Object.freeze({
    "equity contract": "Equity contract",
    "revenue share": "Revenue share",
    SimpleLoan: "Simple loan",
  }),
  preferred_funding_resource: Object.freeze({ crowd: "Crowdfunding", self: "On my own" }),
  regulation_track: Object.freeze({ reg_cf: "Reg CF", rule_506c: "506(c)" }),
  oversubscription_allocation_method: Object.freeze({
    first_come_first_served: "First come, first served",
    pro_rata: "Pro rata",
    issuer_discretion: "At the issuer's discretion",
    other: "Other",
  }),
});

export function optionLabel(key, value) {
  const field = String(key == null ? "" : key);
  const options = OPTION_LABELS[field];
  if (!options || typeof value !== "string") return null;
  return Object.prototype.hasOwnProperty.call(options, value) ? options[value] : null;
}

// Per-track config fields (economic_track_configs[n].<field>).
const TRACK_FIELD_LABELS = Object.freeze({
  regulation_track: "track",
  goal: "target raise",
  min_goal: "minimum to close",
  max_goal: "maximum raise",
  has_lower_minimum_to_close: "lower minimum to close",
  accepts_above_target: "above-target investments",
  oversubscription_allocation_method: "oversubscription allocation",
  oversubscription_allocation_details: "oversubscription details",
  minimum_investment: "minimum investment",
  share_price: "share price",
  investment_multiple: "investment increment",
  term_of_investment: "term",
  funding_round_end: "close date",
  use_of_funds_json: "use of funds",
  min_use_of_funds_json: "use of funds (minimum)",
  max_use_of_funds_json: "use of funds (maximum)",
  card_details: "card details",
});

// security_terms.<column>
const SECURITY_TERM_LABELS = Object.freeze({
  valuation_cap: "Valuation cap",
  discount_rate_pct: "Discount rate",
  mfn: "Most favored nation",
  interest_rate_pct: "Interest rate",
  maturity_months: "Maturity (months)",
  conversion_trigger: "Conversion trigger",
  qualified_financing_min: "Qualified financing minimum",
  payment_frequency: "Payment frequency",
  repayment: "Repayment",
  secured: "Secured",
  collateral_text: "Collateral",
  liquidation_preference_multiple: "Liquidation preference",
  participating: "Participating",
  dividend_rate_pct: "Dividend rate",
  revenue_share_pct: "Revenue share",
  repayment_cap_multiple: "Repayment cap",
  other_text: "Description of the security",
});

// The 1-based index of economic_track_configs entries: the builder and the
// admin Terms editor both send Reg CF first, then 506(c).
const TRACK_BY_INDEX = Object.freeze({ 1: "Reg CF", 2: "506(c)" });

// "some_key" / "someKey" -> "Some key" (never the raw key).
export function humanizeKey(key) {
  const text = String(key == null ? "" : key)
    .replace(/_cents$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!text) return "Field";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function lowerFirst(text) {
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

// The plain-language label for a leaf path as produced by
// listing-save `unsavedLeaves` (`key`, `key.sub`, `key[2].sub`).
export function fieldLabel(path) {
  const raw = String(path == null ? "" : path);
  if (FIELD_LABELS[raw]) return FIELD_LABELS[raw];
  const tokens = [];
  raw.replace(/([^.[\]]+)|\[(\d+)\]/g, (match, name, index) => {
    tokens.push(index !== undefined ? Number(index) : name);
    return match;
  });
  if (!tokens.length) return humanizeKey(raw);
  const [top, ...rest] = tokens;
  if (top === "economic_track_configs" && typeof rest[0] === "number") {
    const track = TRACK_BY_INDEX[rest[0]] || `Track ${rest[0]}`;
    const field = rest[1];
    if (field === undefined) return `${track} terms`;
    const sub = TRACK_FIELD_LABELS[field] || lowerFirst(humanizeKey(field));
    return joinLabel(`${track} ${sub}`, rest.slice(2));
  }
  if (top === "security_terms" && typeof rest[0] === "string") {
    return joinLabel(SECURITY_TERM_LABELS[rest[0]] || humanizeKey(rest[0]), rest.slice(1));
  }
  return joinLabel(FIELD_LABELS[top] || humanizeKey(top), rest);
}

// "FAQs" + [1, "question"] -> "FAQs 1 · question".
function joinLabel(head, rest) {
  let out = head;
  rest.forEach((t) => {
    out += typeof t === "number" ? ` ${t}` : ` · ${lowerFirst(humanizeKey(t))}`;
  });
  return out;
}

// Banner text for any update_home error: the lock message plus the locked
// fields, or the server message, or the fallback.
export function updateErrorMessage(error, fallback) {
  const locked = termsLockedMessage(error);
  if (locked) {
    const labels = [...new Set(termsLockedFields(error).map(termFieldLabel))];
    return labels.length ? `${locked} (${labels.join(", ")})` : locked;
  }
  const data = error && error.response && error.response.data;
  return (data && data.message) || fallback;
}
